import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { apiClient } from '../api/client'
import { getTelegramUserId } from '../utils/telegram'
import { requireCredit } from '../utils/billing'
import { trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'
import { useTokenPrices } from '../hooks/useTokenPrices'
import { TokenFollowManager } from '../components/TokenFollowManager'

interface FollowItem {
  event_id: string
  event_instance_id?: string
  date: string
  event_title: string
  narrative_name: string
  created_at?: string
  followed_at?: string
  status?: string
}

interface FollowsResponse {
  follows: FollowItem[]
  count: number
}

interface TokenFollowsResponse {
  tokens: string[]
  count: number
  active_count: number
  max_count: number
}

interface TelegramDigestPreferenceResponse {
  enabled: boolean
}

export function MyFollowsPage() {
  const navigate = useNavigate()
  const { lang, t } = useLanguage()
  const [follows, setFollows] = useState<FollowItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRemoving, setIsRemoving] = useState<string | null>(null)
  const [tokenFollows, setTokenFollows] = useState<string[]>([])
  const [maxTokenFollows, setMaxTokenFollows] = useState(5)
  const [busyToken, setBusyToken] = useState<string>()
  const [telegramDigestEnabled, setTelegramDigestEnabled] = useState(true)
  const [isSavingDigestPreference, setIsSavingDigestPreference] = useState(false)
  const { data: tokenMarketData } = useTokenPrices()
  const loadStartedRef = useRef(false)
  const loadedTrackedRef = useRef(false)

  const loadFollows = async () => {
    if (loadStartedRef.current) return
    loadStartedRef.current = true
    setIsLoading(true)
    try {
      const data = await apiClient.get<FollowsResponse>('/api/user/follows', {
        chat_id: getTelegramUserId(),
        lang,
      })
      setFollows(data.follows || [])
      if (!loadedTrackedRef.current) {
        loadedTrackedRef.current = true
        void trackBusinessEvent('my_follows_loaded', {
          follow_count: data.count || 0,
          event_ids: (data.follows || []).map((item) => item.event_id),
          is_empty: (data.follows || []).length === 0,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStartedRef.current = false
    loadFollows().catch((error) => {
      console.error('Failed to load follows:', error)
      setIsLoading(false)
    })
  }, [lang])

  useEffect(() => {
    apiClient.get<TokenFollowsResponse>('/api/user/follows/tokens', {
      chat_id: getTelegramUserId(),
    }).then((data) => {
      setTokenFollows(data.tokens || [])
      setMaxTokenFollows(data.max_count || 5)
    }).catch((error) => {
      console.error('Failed to load token follows:', error)
    })
    apiClient.get<TelegramDigestPreferenceResponse>('/api/user/preferences/telegram-token-digest', {
      chat_id: getTelegramUserId(),
    }).then((data) => {
      setTelegramDigestEnabled(data.enabled)
    }).catch((error) => {
      console.error('Failed to load Telegram digest preference:', error)
    })
  }, [])

  const handleDigestPreference = async () => {
    const next = !telegramDigestEnabled
    setIsSavingDigestPreference(true)
    try {
      const data = await apiClient.put<TelegramDigestPreferenceResponse>(
        '/api/user/preferences/telegram-token-digest',
        { chat_id: getTelegramUserId(), enabled: next },
      )
      setTelegramDigestEnabled(data.enabled)
      WebApp.HapticFeedback.selectionChanged()
    } catch (error) {
      console.error('Failed to save Telegram digest preference:', error)
      WebApp.showAlert(t('保存 Telegram 推送设置失败', 'Failed to save Telegram notification setting'))
    } finally {
      setIsSavingDigestPreference(false)
    }
  }

  const handleFollowToken = async (token: string) => {
    setBusyToken(token)
    try {
      const data = await apiClient.post<TokenFollowsResponse & { token: string }>(
        '/api/user/follow/token',
        { chat_id: getTelegramUserId(), token },
      )
      setTokenFollows(data.tokens || [])
      WebApp.HapticFeedback.notificationOccurred('success')
    } catch (error) {
      console.error('Failed to follow token:', error)
      WebApp.showAlert(
        error instanceof Error
          ? error.message
          : t('关注 Token 失败，请稍后重试', 'Failed to follow token. Please try again later.'),
      )
    } finally {
      setBusyToken(undefined)
    }
  }

  const handleUnfollowToken = async (token: string) => {
    setBusyToken(token)
    try {
      const data = await apiClient.delete<TokenFollowsResponse>(
        `/api/user/follow/token?chat_id=${encodeURIComponent(getTelegramUserId())}&token=${encodeURIComponent(token)}`,
      )
      setTokenFollows(data.tokens || [])
      WebApp.HapticFeedback.notificationOccurred('success')
    } catch (error) {
      console.error('Failed to unfollow token:', error)
      WebApp.showAlert(t('取消关注 Token 失败，请稍后重试', 'Failed to unfollow token. Please try again later.'))
    } finally {
      setBusyToken(undefined)
    }
  }

  const handleRemove = async (item: FollowItem) => {
    const followKey = item.event_instance_id || item.event_id
    setIsRemoving(followKey)
    trackBusinessEvent('my_follows_unfollow_click', {
      event_id: item.event_id,
      event_instance_id: item.event_instance_id,
      follow_count: follows.length,
      action_source: 'my_follows',
    }, item.event_id)
    try {
      const query = new URLSearchParams({
        event_id: item.event_id,
        event_instance_id: item.event_instance_id || '',
        date: item.date,
        narrative_name: item.narrative_name || '',
      })
      await apiClient.delete(`/api/user/follow/event?${query.toString()}`)
      setFollows((items) => items.filter((follow) => {
        if (item.event_instance_id) {
          return follow.event_instance_id !== item.event_instance_id
        }
        return follow.event_id !== item.event_id
      }))
      WebApp.HapticFeedback.notificationOccurred('success')
    } catch (error) {
      console.error('Failed to remove follow:', error)
      WebApp.showAlert(t('取消关注失败，请稍后重试', 'Failed to unfollow. Please try again later.'))
    } finally {
      setIsRemoving(null)
    }
  }

  const handleOpenTimeline = async (item: FollowItem) => {
    trackBusinessEvent('my_follows_timeline_click', {
      event_id: item.event_id,
      narrative: item.narrative_name,
      follow_count: follows.length,
      action_source: 'my_follows',
    }, item.event_id)
    const ok = await requireCredit(
      'view_timeline',
      item.event_id,
      t('查看时间轴', 'View Timeline'),
      (message) => WebApp.showAlert(message)
    )
    if (!ok) return

    navigate(`/event/${item.event_id}/timeline-chat?date=${item.date}&narrative=${encodeURIComponent(item.narrative_name || '')}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ←
          </button>
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">{t('我的关注', 'My Follows')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(`Token ${tokenFollows.length}/${maxTokenFollows} · 事件 ${follows.length}/3`, `${tokenFollows.length}/${maxTokenFollows} tokens · ${follows.length}/3 events`)}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-3">
        <TokenFollowManager
          symbols={tokenMarketData?.symbols || []}
          followedTokens={tokenFollows}
          maxCount={maxTokenFollows}
          busyToken={busyToken}
          onFollow={handleFollowToken}
          onUnfollow={handleUnfollowToken}
        />

        <div className="flex items-center justify-between gap-4 px-1 py-2">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {t('Telegram 每日摘要', 'Daily Telegram summary')}
            </div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {t('每日发布后，私聊提醒关注 Token 的新事件', 'A private summary after each daily release')}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={telegramDigestEnabled}
            disabled={isSavingDigestPreference}
            onClick={handleDigestPreference}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${telegramDigestEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${telegramDigestEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <h2 className="pt-2 text-sm font-semibold text-gray-900 dark:text-white">
          {t('已关注事件', 'Followed Events')}
        </h2>

        {isLoading && (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">{t('加载中...', 'Loading...')}</div>
        )}

        {!isLoading && follows.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{t('还没有关注事件', 'No followed events yet')}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('在首页点击“帮我关注”后，会出现在这里。', 'Events you follow from the home page will appear here.')}</p>
          </div>
        )}

        {!isLoading && follows.map((item) => (
          <article
            key={item.event_instance_id || item.event_id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
          >
            <div className="min-w-0">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {item.date} · {item.narrative_name || t('未分类', 'Uncategorized')}
              </div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                {item.event_title || item.event_id}
              </h2>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => {
                  trackBusinessEvent('my_follows_explain_click', {
                    event_id: item.event_id,
                    narrative: item.narrative_name,
                    follow_count: follows.length,
                    action_source: 'my_follows',
                  }, item.event_id)
                  navigate(`/event/${item.event_id}/explain?date=${item.date}&narrative=${encodeURIComponent(item.narrative_name || '')}`)
                }}
                className="px-2 py-2 rounded-md text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
              >
                {t('展开说说', 'Explain')}
              </button>
              <button
                type="button"
                onClick={() => handleOpenTimeline(item)}
                className="px-2 py-2 rounded-md text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
              >
                {t('查看时间轴', 'View Timeline')}
              </button>
              <button
                type="button"
                disabled={isRemoving === (item.event_instance_id || item.event_id)}
                onClick={() => handleRemove(item)}
                className="px-2 py-2 rounded-md text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 disabled:opacity-60"
              >
                {isRemoving === (item.event_instance_id || item.event_id) ? t('取消中', 'Removing') : t('取消关注', 'Unfollow')}
              </button>
            </div>
          </article>
        ))}
      </main>
    </div>
  )
}
